import { useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import sortBy from 'lodash/sortBy';
import { Cart } from 'shared/types/cart';
import { PersonalizeOptions, WarrantyOptions, CartAndError } from 'shared/types/cart/Cart';
import { Discount } from 'shared/types/cart/Discount';
import { Order } from 'shared/types/cart/Order';
import { PriorPromoCodesResult } from 'shared/types/cart/PriorPromoCode';
import { Variant } from 'shared/types/product';
import { SOASubmitOrderResponse } from 'shared/types/soa/order/place-order';
import useSWR, { mutate } from 'swr';
import useI18n from '@/frontastic/hooks/useI18n';
import { callActionMiddleware } from '@/helpers/utils/callActionHelper';
import { getAgentName, getSelectedDivision } from '@/helpers/utils/siteUtils';
import {
  AdditionalDetailsPayload,
  AdjustPricePayload,
  AdjustmentPayload,
  ShippingDiscountPayload,
  UpdateCartPayload,
  UpdateLineItem,
  ValidatePaymentBatchPayload,
  AddCartItemPayload,
  ReplaceCartLineItemPayload,
  UpdateCartItemPayload,
  InsuranceItemPayload,
} from '@/sdk/composable-commerce/types/payloads/CartPayloads';
import { GetPriorPromoCodesQuery } from '@/sdk/composable-commerce/types/queries/CartQueries';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import { CartDetails, UseCartReturn } from './types';

export const CART_SWR_KEY = '/action/cart/getCart';
export const CART_CHECKOUT_SWR_KEY = '/action/cart/getCart?skipSetAvailability=true';

const mutateCartCache = (data?: unknown, opts?: { revalidate?: boolean }) => {
  if (data === undefined) {
    mutate(CART_SWR_KEY);
    mutate(CART_CHECKOUT_SWR_KEY);
    return;
  }
  mutate(CART_SWR_KEY, data, opts);
  mutate(CART_CHECKOUT_SWR_KEY, data, opts);
};
export const revalidateOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  revalidateOnMount: false,
};

const useCart = (fetchShippingMethods: boolean): UseCartReturn => {
  const extensions = sdk.composableCommerce;
  const { currency } = useI18n();
  const pathname = usePathname();
  const isCheckoutPage = pathname?.includes('/checkout') ?? false;
  const cartSwrKey = isCheckoutPage ? CART_CHECKOUT_SWR_KEY : CART_SWR_KEY;
  let shippingMethods: any;
  const { data: brandData, isLoading: isBrandLoading } = useSWR(
    '/action/account/getIsRetiredBrand',
    extensions.account.getIsRetiredBrand,
    {},
  );
  const isRetiredBrand = brandData?.isError ? false : Boolean(brandData?.data?.isRetiredBrand);
  const result = useSWR(
    cartSwrKey,
    () =>
      extensions.cart.getCart({
        query: isCheckoutPage ? { skipSetAvailability: 'true' } : undefined,
      }),
    {},
  );
  const data =
    isRetiredBrand || isBrandLoading ? {} : result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };
  const isLoading = isBrandLoading || (!isRetiredBrand && result.isLoading);
  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const hasOutOfStockItems = !!data?.data?.lineItems?.some((lineItem) => !lineItem.variant?.isOnStock);

  const transaction = useMemo(() => mapCosts({ reference: 'cart', cart: data.data, currency }), [data.data, currency]);

  if (fetchShippingMethods) {
    const shippingMethodsResults = useSWR(
      '/action/cart/getAvailableShippingMethods',
      extensions.cart.getAvailableShippingMethods,
      {
        ...revalidateOptions,
        revalidateOnMount: true,
      },
    );

    shippingMethods = shippingMethodsResults.data?.isError
      ? {}
      : { data: sortBy(shippingMethodsResults.data?.data, ['displayOrder']) };
  }

  const addItem = useCallback(
    async (
      variant: Variant,
      quantity: number,
      isSpecialProduct: boolean,
      personalizeOptions?: PersonalizeOptions,
      warrantyOptions?: WarrantyOptions,
      crossSell?: boolean,
      upSell?: boolean,
      notesPayload?: any,
    ) => {
      const extensions = sdk.composableCommerce;

      const payload: AddCartItemPayload = {
        variant: {
          sku: variant.sku,
          count: quantity,
        },
        isSpecialProduct: isSpecialProduct,
        siteId: getSelectedDivision(),
        crossSell: crossSell,
        upSell: upSell,
      };
      if (notesPayload) {
        payload.notesPayload = notesPayload;
      }
      if (personalizeOptions) {
        payload.personalizeOptions = personalizeOptions;
      }
      if (warrantyOptions) {
        payload.warrantyOptions = warrantyOptions;
      }

      const res = await extensions.cart.addItem(payload);
      if (!res.isError && (res.data as Cart).cartId) {
        mutateCartCache(res, { revalidate: false });
        return res?.data;
      } else {
        throw new Error('Error adding item to cart');
      }
    },
    [],
  );

  const orderCart = useCallback(
    async (
      cardDetails: any,
      contactInformation: any,
      tokenizeData: any,
      checkoutExtraDetails: any,
      divisionCode: string,
    ) => {
      const res = await callActionMiddleware({
        actionName: 'cart/checkout',
        payload: {
          cardDetails,
          contactInformation,
          tokenizeData,
          checkoutExtraDetails,
        },
        query: {
          divisionCode: divisionCode,
          X_INITIATING_AGENT: getAgentName(),
        },
      });
      mutateCartCache();

      if (!res?.isError) {
        return res?.data as Order;
      } else {
        throw new Error(res?.error?.message);
      }
    },
    [],
  );

  const submitSoaOrder = useCallback(async (orderId: string) => {
    const res = await sdk.callAction({
      actionName: 'cart/submitSoaOrder',
      payload: orderId,
    });
    if (!res.isError) {
      return res.data as SOASubmitOrderResponse;
    } else {
      throw new Error(res.error.message);
    }
  }, []);

  const getOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ orderIds: [orderId] });
    mutateCartCache();

    return (res.isError ? {} : res.data.items?.[0]) as Order;
  }, []);

  const getCheckoutOrder = useCallback(async (orderId: string) => {
    const res = await callActionMiddleware({ actionName: 'cart/getCheckoutOrder', query: { orderId: orderId } });
    mutate('/action/cart/getCheckoutOrder');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const orderHistory = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders();

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, []);

  const removeItem = useCallback(async (lineItemId: string, warrantyItemId?: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: { id: lineItemId },
      warrantyItemId: warrantyItemId,
    };

    const res = await extensions.cart.removeItem(payload);
    if (!res.isError && (res.data as Cart).cartId) {
      mutateCartCache(res, { revalidate: false });
      return res?.data;
    } else {
      throw new Error('Error while removeItem');
    }
  }, []);

  const updateItem = useCallback(
    async (
      lineItemId: string,
      newQuantity: number,
      oldQty: number,
      warrantyOptions?: WarrantyOptions,
      personalizeOptions?: PersonalizeOptions,
    ) => {
      const payload: UpdateCartItemPayload = {
        lineItem: {
          id: lineItemId,
          count: newQuantity,
        },
        oldQty: oldQty,
        warrantyOptions: warrantyOptions,
      };
      if (personalizeOptions) {
        payload.personalizeOptions = personalizeOptions;
      }
      const res = await extensions.cart.updateItem(payload);
      if (!res.isError && (res.data as Cart).cartId) {
        mutateCartCache(res, { revalidate: false });
        return res?.data;
      } else {
        throw new Error('Error while updateLineItem');
      }
    },
    [],
  );

  const updateCart = useCallback(async (payload: UpdateCartPayload): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.updateCart(payload);

    mutateCartCache(res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const updateLineItem = useCallback(async (payload: UpdateLineItem): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.updateLineItem(payload);

    mutateCartCache(res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setShippingMethod = useCallback(async (shippingMethodId: string): Promise<Cart> => {
    const payload = {
      shippingMethod: {
        id: shippingMethodId,
      },
    };
    const res = await extensions.cart.setShippingMethod(payload);
    if (!res.isError) {
      mutateCartCache(res, { revalidate: false });
      return res?.data as Cart;
    } else {
      throw new Error('Error in setting shipping method');
    }
  }, []);

  const redeemDiscountCode = useCallback(async (code: string): Promise<CartAndError> => {
    const extensions = sdk.composableCommerce;
    const payload = {
      code: code,
    };
    const res = await extensions.cart.redeemDiscountCode(payload);
    if (!res.isError && (res.data?.cart as Cart).cartId) {
      mutateCartCache();
      return res?.data as CartAndError;
    } else {
      throw new Error('code not valid');
    }
  }, []);

  const removeDiscountCode = useCallback(async (discount: Discount): Promise<Cart> => {
    const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });
    if (!res.isError && (res.data as Cart).cartId) {
      mutateCartCache(res, { revalidate: false });
      return res?.data as Cart;
    } else {
      throw new Error('code not valid');
    }
  }, []);

  const applyShippingDiscount = useCallback(async (payload: ShippingDiscountPayload) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.applyShippingDiscount(payload);
    mutateCartCache(res);
  }, []);

  const applyAdjustments = useCallback(async (payload: AdjustmentPayload) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.applyAdjustments(payload);
    mutateCartCache(res);
  }, []);

  const freezeCart = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.freezeCart();
    mutateCartCache(res);
  }, []);

  const unfreezeCart = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.unfreezeCart();
    mutateCartCache(res);
  }, []);

  const adjustLineItemPrice = useCallback(async (payload: AdjustPricePayload) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.adjustLineItemPrice(payload);
    mutateCartCache(res);
  }, []);
  const addAdditionalDetails = useCallback(async (payload: AdditionalDetailsPayload) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.addAdditionalDetails(payload);
    mutateCartCache(res);
  }, []);

  const cancelOrder = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.cancelOrder();
    mutateCartCache(res);
  }, []);

  const getCustomerCart = useCallback(async (payload: CartDetails) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.getCartsByOmsCustomerId(payload);
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const getHoldCart = useCallback(async (payload: any) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.getHoldCart(payload);
    mutateCartCache(res);
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const resetCart = useCallback(async (payload: any) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.resetCart(payload);
    if (!res.isError && (res.data as Cart).cartId) {
      mutateCartCache(res, { revalidate: false });
      sessionStorage.setItem('openedOnceForOrder', 'false');
      sessionStorage.setItem('emailSetOnceForSession', 'false');
      return res?.data;
    } else {
      throw new Error('Error while resetCart');
    }
  }, []);

  const validatePaymentBatch = useCallback(async (payload: ValidatePaymentBatchPayload) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.validatePaymentBatch(payload);
    return res.isError ? {} : res.data;
  }, []);

  const beginNoChargeOrder = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.beginNoChargeOrder();
    if (!res.isError && (res.data as Cart).cartId) {
      mutateCartCache(res, { revalidate: false });
      sessionStorage.setItem('openedOnceForOrder', 'false');
      sessionStorage.setItem('emailSetOnceForSession', 'false');
      return res?.data;
    } else {
      throw new Error('Error while beginNoChargeOrder');
    }
  }, []);

  const replaceCartLineItem = useCallback(
    async (
      lineItemId: string,
      quantity: number,
      variant: Variant,
      warrantyOptions?: WarrantyOptions,
      personalizeOptions?: PersonalizeOptions,
      siteId?: string,
    ) => {
      const payload: ReplaceCartLineItemPayload = {
        variant: { sku: variant?.sku, quantity: quantity },
        lineItem: { id: lineItemId },
        warrantyOptions: warrantyOptions,
        siteId: siteId,
      };
      if (personalizeOptions) {
        payload.personalizeOptions = personalizeOptions;
        payload.isSpecialProduct = true;
      }
      const res = await extensions.cart.replaceCartLineItem(payload);
      if (!res.isError && (res.data as Cart).cartId) {
        mutateCartCache(res, { revalidate: false });
        return res?.data;
      } else {
        throw new Error('Error while replaceCartLineItem');
      }
    },
    [],
  );

  const revalidateCart = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.revalidateCart();
    mutateCartCache(res);
  }, []);

  const getAvailableShippingMethods = useCallback(async () => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.getAvailableShippingMethods();
    if (!res.isError) {
      return res?.data;
    } else {
      throw new Error('Error retrieving available shipping methods');
    }
  }, []);

  const getLineItemsAvailibilty = useCallback(async () => {
    const res = await extensions.cart.getLineItemsAvailibilty();
    if (!res.isError) {
      return res?.data;
    } else {
      throw new Error('Error retrieving LineItemsAvailibilty');
    }
  }, []);

  const handleShippingInsurance = useCallback(async (payload: InsuranceItemPayload): Promise<Cart> => {
    const res = await extensions.cart.handleShippingInsurance(payload);
    if (!res.isError && (res.data as Cart).cartId) {
      mutateCartCache(res, { revalidate: false });
      return res?.data;
    } else {
      return {} as Cart;
    }
  }, []);
  const getPriorPromoCodes = useCallback(async (query?: GetPriorPromoCodesQuery): Promise<PriorPromoCodesResult> => {
    const res = await extensions.cart.getPriorPromoCodes(query);
    if (!res.isError) {
      return res?.data;
    }
    throw new Error('Error retrieving prior promo codes');
  }, []);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems,
    transaction,
    addItem,
    updateCart,
    setShippingMethod,
    removeItem,
    updateItem,
    shippingMethods,
    orderCart,
    getOrder,
    getCheckoutOrder,
    orderHistory,
    redeemDiscountCode,
    removeDiscountCode,
    applyShippingDiscount,
    applyAdjustments,
    freezeCart,
    unfreezeCart,
    adjustLineItemPrice,
    addAdditionalDetails,
    cancelOrder,
    getCustomerCart,
    getHoldCart,
    resetCart,
    validatePaymentBatch,
    updateLineItem,
    beginNoChargeOrder,
    replaceCartLineItem,
    revalidateCart,
    getAvailableShippingMethods,
    submitSoaOrder,
    getLineItemsAvailibilty,
    handleShippingInsurance,
    getPriorPromoCodes,
    isLoading,
  };
};

export default useCart;
